import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppButton } from "@/components/landing/WhatsAppButton";
import { usePageMeta } from "@/seo/usePageMeta";
import { SERVICES_DATA } from "@/data/servicesData";

export default function SegmentPage({ segment }) {
  usePageMeta({
    title: segment.metaTitle,
    description: segment.metaDescription,
    path: `/segments/${segment.slug}/`,
  });

  const services = SERVICES_DATA.filter((s) => segment.services.includes(s.slug));

  return (
    <div data-testid={`segment-page-${segment.slug}`}>
      <Header />
      <main>
        <section className="py-20 lg:py-28">
          <div className="container-x">
            <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-zinc-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-zinc-900 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-zinc-900">{segment.name}</span>
            </nav>
            <motion.div
              initial={{ y: 24 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-brand-primary mb-4">
                Who We Serve
              </p>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none text-zinc-900">
                {segment.h1}
              </h1>
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
              />
            </motion.div>
            <p className="mt-8 text-base leading-relaxed text-zinc-600 max-w-2xl">{segment.intro}</p>

            <h2 className="mt-16 text-2xl font-extrabold tracking-tight text-zinc-900">
              Most requested for {segment.name.toLowerCase()} properties
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}/`}
                  data-testid={`segment-service-${s.slug}`}
                  className="group bg-white border border-zinc-200 rounded-md p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60 hover:shadow-[0_8px_30px_rgba(212,175,55,0.18)]"
                >
                  <h3 className="text-lg font-bold tracking-tight text-zinc-900 group-hover:text-brand-primary transition-colors">
                    {s.name}
                  </h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                    Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-black grain relative">
          <div className="container-x flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">Ready to get started?</h2>
              <p className="mt-3 text-white/70 text-base">Free, no-obligation quotes — call us or send the form.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#quote"
                data-testid="segment-quote-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+15068714144"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white/10 border border-white/25 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
              >
                <Phone className="w-4 h-4" /> (506) 871-4144
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
